import { Col, Form, Spin } from "antd";
import { useAppDispatch } from "core/utils";
import i18n from "i18next";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";

import type { DynamicPropertyGroupEntity } from "src/app/features/DynamicPropertyGroup/types/DynamicPropertyGroupEntity";
import { getAssignedDataGroup } from "../../../../core/router/auth";
import { ResourceName } from "../../../../core/router/config/ResourceName";
import { setIsDynamicPropertiesLoading } from "../../../../core/setup/redux/state/dynamicPropertiesLoading/dynamicPropertiesLoadingState";
import type { ID } from "../../../../core/types/fe";
import type { DynamicPropertyEntity } from "../../../../core/types/fe/entities/dynamicPropertyEntity";
import { ConfirmationModal } from "../../../Modal/ConfirmationModal";
import { createQueryParams, useGetTableDataQuery } from "../../../Table";
import type { FetchDataQuery } from "../../../Table/types/types";
import { propertyGroupRequiredRule } from "../../components/Validations/validationRules/PropetyGroupRequired";
import type { AntCustomFormField } from "../../types/types";
import { DynamicPropertiesRender } from "./DynamicPropertiesRender";
import { PropertyGroupSelector } from "./PropertyGroupSelector";

interface DynamicPropertiesProps extends AntCustomFormField<DynamicPropertyEntity> {
    label: string;
    readOnly: boolean;
    useReadOnly?: boolean;
    hasRequiredRule?: boolean;
}

export const DynamicProperties: FC<DynamicPropertiesProps> = ({ readOnly, value, label, onChange, useReadOnly, hasRequiredRule }) => {
    const dispatch = useAppDispatch();
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [pendingNewId, setPendingNewId] = useState<ID | undefined>(undefined);
    const selectedDataGroupCode = getAssignedDataGroup()?.code;
    const baseURI = `data-groups/${selectedDataGroupCode}`;
    const resourceFetchPath = `${baseURI}/${ResourceName.DYNAMIC_PROPERTY_GROUPS}`;

    const queryConfig = {
        pageSize: 1000,
        currentPage: 1,
        filters: { status: "ACTIVE" },
        sorter: [],
    };
    const query = createQueryParams(queryConfig);

    const fetchDataQuery = {
        query,
        resourceFetchPath,
        dataMapperKey: "",
    } as FetchDataQuery;

    const { data, isFetching } = useGetTableDataQuery(fetchDataQuery, {
        skip: false,
    });

    useEffect(() => {
        dispatch(setIsDynamicPropertiesLoading(isFetching));
        return () => {
            dispatch(setIsDynamicPropertiesLoading(false));
        };
    }, [dispatch, isFetching]);

    const options: DynamicPropertyGroupEntity[] = useMemo(() => data?.list || [], [data?.list]);

    const proceedWithSelection = useCallback(
        (newId: ID | undefined) => {
            const findOptionById = (id: string | number): DynamicPropertyGroupEntity | undefined => {
                return options.find((option) => option.id === id);
            };

            const propertyGroup: DynamicPropertyGroupEntity | undefined = findOptionById(newId as string);

            const updateValue = (newValue: DynamicPropertyEntity) => {
                if (onChange) {
                    onChange(newValue);
                }
            };

            if (!propertyGroup) {
                // Updates the value when it's clear
                const nextValue: DynamicPropertyEntity = {
                    dynamicPropertyGroupValue: undefined,
                    dynamicPropertyValues: [],
                };
                updateValue(nextValue);
                return;
            }

            const newRenderProps = propertyGroup.dynamicPropertyToGroups?.map((prop) => {
                const propValue = value?.dynamicPropertyValues?.find((val) => val.key === prop.dynamicPropertyId);

                return {
                    id: prop.dynamicPropertyId,
                    key: prop.key,
                    label: prop.name,
                    type: prop.dataType,
                    value: propValue?.value,
                    readOnly: propValue?.readOnly,
                    pattern: prop.validationPattern,
                    mandatory: prop.mandatory,
                };
            });

            const propertyGroupValue = {
                id: propertyGroup?.id,
                name: propertyGroup?.name,
            };

            const nextValue: DynamicPropertyEntity = {
                dynamicPropertyGroupValue: propertyGroupValue,
                dynamicPropertyValues: newRenderProps,
            };

            updateValue(nextValue);
        },
        [value?.dynamicPropertyValues, onChange, options],
    );

    const onSelected = useCallback((newId: ID | undefined) => {
        setPendingNewId(newId);
        setIsConfirmModalOpen(true);
    }, []);

    const onConfirmSelection = useCallback(() => {
        setIsConfirmModalOpen(false);
        proceedWithSelection(pendingNewId);
        setPendingNewId(undefined);
    }, [pendingNewId, proceedWithSelection]);

    const onCancelSelection = useCallback(() => {
        setIsConfirmModalOpen(false);
        setPendingNewId(undefined);
    }, []);

    const rules = hasRequiredRule ? [propertyGroupRequiredRule] : [];

    return (
        <Col span={21} key={`${label}PropertyGroupSelectorFormItemCol`}>
            <Form.Item key={`${label}FormItem`} rules={rules} name={["dynamicProperties", "dynamicPropertyGroupValue"]}>
                <Spin spinning={isFetching}>
                    <PropertyGroupSelector
                        onSelect={onSelected}
                        readOnly={readOnly}
                        dynamicProperty={value?.dynamicPropertyGroupValue}
                        options={options}
                    />
                </Spin>
            </Form.Item>
            <DynamicPropertiesRender value={value?.dynamicPropertyValues} useReadOnly={readOnly ? false : useReadOnly} />
            <ConfirmationModal
                visible={isConfirmModalOpen}
                onOk={onConfirmSelection}
                onCancel={onCancelSelection}
                title={i18n.t("modal.title.confirmation")}
                message={i18n.t("modal.label.dynamicPropertyConfirmChange")}
            />
        </Col>
    );
};
